import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth, IMPERSONABLE_ROLES } from '../../context/AuthContext';
import RoleImpersonationModal from './RoleImpersonationModal';

const {
  FiShield, FiX, FiChevronDown, FiEye, FiHeart, FiHome,
  FiShoppingBag, FiGitBranch, FiBriefcase, FiRefreshCw,
} = FiIcons;

const ICON_MAP = {
  FiEye, FiHeart, FiHome, FiShoppingBag, FiGitBranch, FiBriefcase,
};

const ImpersonationBar = () => {
  const { permissions, isImpersonating, impersonatedRole, stopImpersonation, startImpersonation } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  if (!permissions.isTrueSuperAdmin()) return null;

  const currentRole = IMPERSONABLE_ROLES.find(r => r.id === impersonatedRole);

  return (
    <>
      <AnimatePresence>
        {isImpersonating ? (
          <motion.div key="bar"
            initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-[500] bg-gradient-to-r from-orange-500 to-amber-500
                       text-white shadow-2xl shadow-orange-500/30">
            <div className="container mx-auto px-6 py-2.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiShield} className="w-4 h-4" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest opacity-80 flex-shrink-0">
                  Impersonating:
                </span>
                {currentRole && (
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      {ICON_MAP[currentRole.icon] && (
                        <SafeIcon icon={ICON_MAP[currentRole.icon]} className="w-3.5 h-3.5" />
                      )}
                    </div>
                    <span className="text-sm font-black flex-shrink-0">{currentRole.label}</span>
                    <span className="hidden md:block text-[10px] opacity-70 font-light truncate">
                      — {currentRole.desc}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setModalOpen(true)}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-white/20 hover:bg-white/30
                             rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border border-white/20">
                  <SafeIcon icon={FiRefreshCw} className="w-3 h-3" />
                  Switch
                </button>
                <button onClick={stopImpersonation}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-white text-orange-600
                             hover:bg-white/90 rounded-xl text-[9px] font-black uppercase tracking-widest
                             transition-all shadow-sm">
                  <SafeIcon icon={FiX} className="w-3 h-3" />
                  Exit
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="pill"
            initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 60 }}
            className="fixed bottom-6 right-6 z-[400]">
            <AnimatePresence>
              {expanded && (
                <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="mb-3 bg-accent-navy text-white rounded-[28px] shadow-2xl
                             shadow-accent-navy/40 border border-white/10 overflow-hidden w-72">
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 bg-accent-gold text-accent-navy rounded-xl flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiShield} className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold">Role Impersonation</p>
                        <p className="text-[9px] text-white/50 font-black uppercase tracking-widest">
                          Super Admin Tool
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-white/60 font-light leading-relaxed mb-4">
                      Preview the UI from any user role's perspective for testing and troubleshooting.
                    </p>
                    <div className="grid grid-cols-3 gap-1.5 mb-4">
                      {IMPERSONABLE_ROLES.map(role => {
                        const IconComp = ICON_MAP[role.icon];
                        return (
                          <button key={role.id}
                            onClick={() => startImpersonation(role.id)}
                            title={role.label}
                            className="flex flex-col items-center gap-1 p-2 bg-white/5 hover:bg-white/15
                                       rounded-xl transition-all border border-white/10 hover:border-white/30 group">
                            {IconComp && <SafeIcon icon={IconComp} className="w-4 h-4 text-accent-gold" />}
                            <span className="text-[8px] font-black text-white/60 group-hover:text-white
                                            uppercase tracking-wide leading-tight text-center">
                              {role.label.replace('Memorial ', '').replace('Estate ', '')}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={() => setModalOpen(true)}
                      className="w-full py-2.5 bg-accent-gold text-accent-navy rounded-xl text-[9px]
                                 font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all">
                      Open Role Picker
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex justify-end">
              <button onClick={() => setExpanded(e => !e)}
                className="flex items-center gap-2 px-5 py-3 bg-accent-navy text-white rounded-2xl
                           shadow-2xl shadow-accent-navy/40 text-[10px] font-black uppercase tracking-widest
                           hover:bg-accent-gold hover:text-accent-navy transition-all border border-white/10">
                <SafeIcon icon={FiShield} className="w-4 h-4" />
                Role Tools
                <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <SafeIcon icon={FiChevronDown} className="w-3.5 h-3.5" />
                </motion.span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <RoleImpersonationModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};

export default ImpersonationBar;